import { useEffect, useState } from "react";
import { View, Text, Card } from "../base";
import { Icon } from "../base/Icon";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";

type CreditsCardProps = {
  onClickAddFunds: () => void;
};

type Transaction = {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  source: string;
  referenceId: string | null;
  description: string | null;
  createdAt: string;
};

type BalanceResponse = {
  data: {
    balance: number;
    organizationId: string;
  };
};

type TransactionsResponse = {
  data: Transaction[];
  metadata: {
    total: number;
    page: number;
    limit: number;
  };
};

const SOURCE_LABELS: Record<string, string> = {
  stripe_payment: "Stripe Payment",
  session_usage: "Session Usage",
  eval_usage: "Eval Usage",
  completions_usage: "Completions Usage",
  manual_adjustment: "Manual Adjustment",
  auto_replenishment: "Auto-Replenishment",
  recurring_charge: "Recurring Charge",
  voice_lab_generation: "Voice Lab Generation",
};

// Sources whose referenceId is an AgentSession public_id, surfaced in the
// "Session" column of the transaction table.
const SESSION_SOURCES: ReadonlySet<string> = new Set(["session_usage", "completions_usage", "eval_usage"]);

// Rounded to cents — used for the big balance header where trailing precision
// would be noise.
const formatMicrodollars = (microdollars: number): string => {
  return `$${(microdollars / 1_000_000).toFixed(2)}`;
};

// Full precision — used for per-row amounts and balances in the transaction
// table so tiny LLM completions costs ($0.000125) aren't hidden by cent
// rounding. Shows 2–6 decimals, trailing zeros trimmed.
const precisionFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});
const formatMicrodollarsPrecise = (microdollars: number): string => {
  return precisionFormatter.format(microdollars / 1_000_000);
};

const PAGE_SIZE = 20;

export const CreditsCard = ({ onClickAddFunds }: CreditsCardProps) => {
  const { organizationId } = useEffectiveOrganization();
  const balanceRequest = useApiRequest<BalanceResponse>();
  const transactionsRequest = useApiRequest<TransactionsResponse>();
  const [page, setPage] = useState(1);
  const [copiedSessionId, setCopiedSessionId] = useState<string | null>(null);

  const copySessionId = async (sessionId: string) => {
    try {
      await navigator.clipboard.writeText(sessionId);
      setCopiedSessionId(sessionId);
      setTimeout(() => {
        setCopiedSessionId((current) => (current === sessionId ? null : current));
      }, 1500);
    } catch {
      // Clipboard API can fail in unfocused tabs or insecure contexts — silent
      // failure is fine, the session id is still visible in the title tooltip.
    }
  };

  useEffect(() => {
    if (organizationId) {
      balanceRequest.fetch(`/v1/credits/balance?organizationId=${organizationId}`);
    }
  }, [organizationId]);

  useEffect(() => {
    if (organizationId) {
      transactionsRequest.fetch(`/v1/credits/transactions?organizationId=${organizationId}&page=${page}&limit=${PAGE_SIZE}`);
    }
  }, [organizationId, page]);

  const balance = balanceRequest.data?.data?.balance ?? 0;
  const transactions = transactionsRequest.data?.data ?? [];
  const totalTransactions = transactionsRequest.data?.metadata?.total ?? 0;
  const totalPages = Math.ceil(totalTransactions / PAGE_SIZE);

  return (
    <View className="flex flex-col gap-5">
      {/* Balance Header */}
      <View className="flex flex-col">
        <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">Balance</Text>
        <Card className="p-6">
          <View className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
            {balanceRequest.isLoading ? (
              <View className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 dark:border-gray-700 border-t-blue-500" />
            ) : (
              <Text
                variant="body"
                className={`text-3xl font-bold ${balance <= 0 ? "text-red-500 dark:text-red-400" : ""}`}
              >
                {formatMicrodollars(balance)} available
              </Text>
            )}
            <button
              onClick={onClickAddFunds}
              className="flex flex-row items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-lg px-2 py-1.5 group mt-3 sm:mt-0"
            >
              <View className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
                <Icon
                  name="add"
                  size={13}
                  className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
                />
              </View>
              <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                Add funds
              </Text>
            </button>
          </View>
        </Card>
      </View>

      {/* Transaction History */}
      <View className="flex flex-col">
        <View className="flex flex-row items-center justify-between mb-3">
          <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Transaction History</Text>
          {totalTransactions > 0 && (
            <Text className="text-xs text-gray-400 dark:text-gray-500">
              {totalTransactions} {totalTransactions === 1 ? "transaction" : "transactions"}
            </Text>
          )}
        </View>

        {transactionsRequest.isLoading && transactions.length === 0 ? (
          <View className="flex flex-col items-center justify-center py-16 gap-3">
            <View className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-700 border-t-blue-500" />
            <Text className="text-sm text-gray-400 dark:text-gray-500">Loading transactions...</Text>
          </View>
        ) : transactionsRequest.error ? (
          <View className="flex flex-row items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
            <Icon
              name="info"
              size={16}
              className="text-red-500 shrink-0"
            />
            <Text className="text-sm text-red-600 dark:text-red-400">Failed to load transactions.</Text>
          </View>
        ) : transactions.length > 0 ? (
          <View className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700/50 overflow-hidden">
            {/* Table header */}
            <View className="flex flex-row items-center px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700/50">
              <Text className="w-32 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</Text>
              <Text className="w-20 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</Text>
              <Text className="w-28 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Amount</Text>
              <Text className="w-28 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right hidden sm:block">Balance</Text>
              <Text className="w-48 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:block pl-4">Source</Text>
              <Text className="w-28 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:block pl-4">Session</Text>
              <Text className="flex-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:block pl-4">Description</Text>
            </View>

            {/* Table rows */}
            {transactions.map((tx, index) => {
              const sessionId = SESSION_SOURCES.has(tx.source) ? tx.referenceId : null;
              return (
                <View
                  key={tx.id}
                  className={`flex flex-row items-center px-4 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/30 ${
                    index < transactions.length - 1 ? "border-b border-gray-100 dark:border-gray-800" : ""
                  }`}
                >
                  <Text className="w-32 text-sm tabular-nums text-gray-600 dark:text-gray-400">{new Date(tx.createdAt).toLocaleDateString()}</Text>
                  <View className="w-20">
                    <Text
                      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        tx.type === "credit"
                          ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400"
                          : tx.amount === 0
                            ? "bg-gray-100 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400"
                            : "bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400"
                      }`}
                    >
                      {tx.type}
                    </Text>
                  </View>
                  <Text
                    className={`w-28 text-sm tabular-nums font-medium text-right ${
                      tx.type === "credit"
                        ? "text-emerald-600 dark:text-emerald-400"
                        : tx.amount === 0
                          ? "text-gray-500 dark:text-gray-400"
                          : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    {tx.type === "credit" ? "+" : "-"}
                    {formatMicrodollarsPrecise(tx.amount)}
                  </Text>
                  <Text className="w-28 text-sm tabular-nums text-gray-600 dark:text-gray-400 text-right hidden sm:block">
                    {formatMicrodollars(tx.balanceAfter)}
                  </Text>
                  <Text className="w-48 text-sm text-gray-600 dark:text-gray-400 hidden md:block pl-4 truncate">{SOURCE_LABELS[tx.source] || tx.source}</Text>
                  <View className="w-28 hidden xl:flex flex-row items-center gap-1.5 pl-4">
                    {sessionId ? (
                      <button
                        type="button"
                        onClick={() => copySessionId(sessionId)}
                        className="flex flex-row items-center gap-1 text-sm tabular-nums text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                        title={copiedSessionId === sessionId ? "Copied" : `Copy ${sessionId}`}
                      >
                        <span className="truncate">{sessionId.slice(0, 8)}</span>
                        <Icon
                          name={copiedSessionId === sessionId ? "check" : "copy"}
                          size={12}
                          className={copiedSessionId === sessionId ? "text-emerald-500" : "text-gray-400 dark:text-gray-500"}
                        />
                      </button>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </View>
                  <Text className="flex-1 text-sm text-gray-500 dark:text-gray-500 hidden lg:block pl-4 truncate">{tx.description || "-"}</Text>
                </View>
              );
            })}
          </View>
        ) : (
          <View className="flex flex-col items-center justify-center py-12 rounded-lg border border-dashed border-gray-200 dark:border-gray-700">
            <Icon
              name="activity"
              size={28}
              className="text-gray-300 dark:text-gray-600 mb-3"
            />
            <Text className="text-sm text-gray-500 dark:text-gray-400 text-center">No transactions yet.</Text>
          </View>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <View className="flex flex-row items-center justify-between mt-3">
            <Text className="text-xs text-gray-400 dark:text-gray-500">
              Page {page} of {totalPages}
            </Text>
            <View className="flex flex-row items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};
